import { useState, useCallback, useRef } from "react";
import {
  capitalizeWords,
  extractRoadFromDisplayName,
  getSearchVariants,
  includesNormalized,
  normalizeRegion,
  startsWithNormalized,
  stripRoadPrefix,
} from "@/lib/geoText";

interface AddressSuggestion {
  display: string;       // Wyświetlana nazwa
  city: string;          // Miasto
  region: string;        // Województwo
  address?: string;      // Ulica (jeśli dostępna)
  postalCode?: string;   // Kod pocztowy
  lat: number;
  lng: number;
  // Internal fields for precise geocoding of house numbers
  _needsPreciseGeocode?: boolean;
  _streetName?: string;
  _houseNumber?: string;
}

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
  addresstype?: string;
  type?: string;
  class?: string;
  name?: string;
  address?: {
    road?: string;
    house_number?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
    state?: string;
    postcode?: string;
  };
}

const isHighwayLike = (item: NominatimResult): boolean => {
  const cls = (item.class || "").toLowerCase();
  const addresstype = (item.addresstype || "").toLowerCase();
  // Najbardziej sensowne dla autouzupełniania ulic
  return cls === "highway" || addresstype === "road";
};

export const useAddressSuggestions = () => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchSuggestions = useCallback(async (query: string, type: "city" | "address", city?: string) => {
    // Anuluj poprzednie żądanie
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Wyczyść poprzedni timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    // Debounce 300ms
    debounceTimerRef.current = setTimeout(async () => {
      setIsLoading(true);
      abortControllerRef.current = new AbortController();

      try {
        // Dla adresów - wyodrębnij numer domu z zapytania (np. "ledochowskiej 5" -> street="ledochowskiej", houseNumber="5")
        let streetQuery = query;
        let userHouseNumber: string | null = null;
        
        if (type === "address") {
          // Szukamy wzorca: tekst + spacja + numer (opcjonalnie z literą, np. "5", "5a", "12/3")
          const houseNumberMatch = query.match(/^(.+?)\s+(\d+[a-zA-Z]?(?:\/\d+[a-zA-Z]?)?)$/);
          if (houseNumberMatch) {
            streetQuery = houseNumberMatch[1].trim();
            userHouseNumber = houseNumberMatch[2];
          }
        }

        const fetchNominatim = async (searchQueryStr: string): Promise<NominatimResult[]> => {
          const params = new URLSearchParams({
            q: searchQueryStr,
            format: "json",
            // Ważne: Nominatim często nie zwraca oczywistych trafień przy krótkich prefiksach,
            // więc pobieramy więcej i filtrujemy/sortujemy po stronie klienta.
            limit: "50",
            countrycodes: "pl",
            addressdetails: "1",
          });

          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?` + params,
            {
              headers: {
                "User-Agent": "GdzieSchronisko-ShelterApp/1.0",
                "Accept-Language": "pl",
              },
              signal: abortControllerRef.current?.signal,
            }
          );

          if (!response.ok) {
            throw new Error("Błąd połączenia");
          }

          return (await response.json()) as NominatimResult[];
        };

        // Używamy ogólnego q= bo Nominatim nie obsługuje częściowych dopasowań dla city=/street=
        // Filtrujemy wyniki po stronie klienta
        // Nominatim wymaga polskich znaków, więc próbujemy warianty
        // Dla adresów używamy streetQuery (bez numeru domu) zamiast pełnego query
        const searchBase = type === "address" ? streetQuery : query;
        const queryVariants = getSearchVariants(searchBase);
        const primaryQuery = queryVariants.length > 1 ? queryVariants[1] : searchBase; // Prefer Polish variant
        
        const searchQuery = type === "city"
          ? `${primaryQuery}, Polska`
          : city?.trim()
            ? `${primaryQuery}, ${city}, Polska`
            : `${primaryQuery}, Polska`;

        let data: NominatimResult[] = await fetchNominatim(searchQuery);
        
        // If no results with Polish variant, try original query
        if (data.length === 0 && queryVariants.length > 1) {
          const fallbackSearchQuery = type === "city"
            ? `${searchBase}, Polska`
            : city?.trim()
              ? `${searchBase}, ${city}, Polska`
              : `${searchBase}, Polska`;
          data = await fetchNominatim(fallbackSearchQuery);
        }

        // Dla filtrowania używamy streetQuery (bez numeru)
        const queryTrimmed = (type === "address" ? streetQuery : query).trim();
        
        // Dla wyszukiwania miast - wyciągnij unikalne miasta z wyników
        if (type === "city") {
          const citiesMap = new Map<string, AddressSuggestion>();
          
          for (const item of data) {
            const lat = parseFloat(item.lat);
            const lng = parseFloat(item.lon);
            if (lat < 49 || lat > 55 || lng < 14 || lng > 24) continue;
            
            // Odfiltruj wyniki typu "state" (województwo)
            const addresstype = (item.addresstype || "").toLowerCase();
            if (addresstype === "state") continue;
            
            const addr = item.address || {};
            const cityNameRaw =
              addr.city ||
              addr.town ||
              addr.village ||
              addr.municipality ||
              item.name ||
              "";
            if (!cityNameRaw) continue;

            if (!startsWithNormalized(cityNameRaw, queryTrimmed)) continue;
            
            const cityKey = cityNameRaw.toLowerCase();
            if (citiesMap.has(cityKey)) continue; // już mamy to miasto
            
            const cityFormatted = capitalizeWords(cityNameRaw);
            const region = normalizeRegion(addr.state);
            
            const parts: string[] = [cityFormatted];
            if (region) parts.push(region);
            const display = parts.join(", ");
            
            citiesMap.set(cityKey, {
              display,
              city: cityFormatted,
              region,
              postalCode: addr.postcode || undefined,
              lat,
              lng,
            });
            
            // Nie breakujemy za wcześnie, bo przy krótkich prefiksach pierwsze wyniki bywają "śmieciowe".
            if (citiesMap.size >= 8) {
              // później i tak ograniczamy do 5
              break;
            }
          }

          let list = Array.from(citiesMap.values())
            .sort((a, b) => a.city.localeCompare(b.city, "pl"))
            .slice(0, 5);

          // Dla miast bez kodu pocztowego - spróbuj pobrać kod przez reverse geocode
          // (Nominatim zwraca kody tylko dla konkretnych adresów, nie dla samych miast)
          const citiesNeedingPostcode = list.filter(c => !c.postalCode);
          if (citiesNeedingPostcode.length > 0) {
            const reverseResults = await Promise.allSettled(
              citiesNeedingPostcode.map(async (city) => {
                try {
                  const reverseParams = new URLSearchParams({
                    lat: city.lat.toString(),
                    lon: city.lng.toString(),
                    format: "json",
                    addressdetails: "1",
                    zoom: "18", // Wysoki zoom = bardziej szczegółowy adres
                  });
                  const reverseResponse = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?${reverseParams}`,
                    {
                      headers: {
                        "User-Agent": "GdzieSchronisko-ShelterApp/1.0",
                        "Accept-Language": "pl",
                      },
                      signal: abortControllerRef.current?.signal,
                    }
                  );
                  if (reverseResponse.ok) {
                    const reverseData = await reverseResponse.json();
                    return { cityKey: city.city.toLowerCase(), postcode: reverseData?.address?.postcode };
                  }
                } catch {
                  // Ignore errors for individual reverse geocodes
                }
                return null;
              })
            );

            // Update cities with fetched postcodes
            for (const result of reverseResults) {
              if (result.status === "fulfilled" && result.value?.postcode) {
                const idx = list.findIndex(c => c.city.toLowerCase() === result.value.cityKey);
                if (idx !== -1) {
                  list[idx] = { ...list[idx], postalCode: result.value.postcode };
                }
              }
            }
          }

          setSuggestions(list);
          return;
        }
        
        // Dla adresów - standardowa logika
        const mapAddressResults = (dataIn: NominatimResult[]): AddressSuggestion[] =>
          dataIn
            .filter((item) => {
            const lat = parseFloat(item.lat);
            const lng = parseFloat(item.lon);
            if (lat < 49 || lat > 55 || lng < 14 || lng > 24) return false;
            
            // Dla adresów - sprawdź czy ulica zawiera zapytanie i czy miasto się zgadza
            if (type === "address") {
              // Odfiltruj sklepy/POI - chcemy głównie drogi/ulice
              if (!isHighwayLike(item)) return false;

              const addr = item.address || {};
              const roadRaw = addr.road || "";
              const displayRaw = item.display_name || "";
              const placeCityContextRaw = [
                addr.city,
                addr.town,
                addr.village,
                addr.municipality,
                addr.county,
              ]
                .filter(Boolean)
                .join(" ");
              
              // Jeśli podano miasto, filtruj tylko wyniki z tego miasta
              if (city) {
                // Porównanie odporne na polskie znaki / wielkość liter
                // Uwaga: Nominatim dla ulic w Warszawie potrafi pominąć "city" i podać tylko "county".
                // Jeśli nie mamy żadnego pola miejskiego, nie odrzucamy wyniku (i tak query zawiera miasto).
                if (placeCityContextRaw) {
                  if (
                    !includesNormalized(placeCityContextRaw, city) &&
                    !includesNormalized(city, placeCityContextRaw)
                  ) {
                    return false;
                  }
                }
              }

              // Dopasowanie (prefiks i „zawiera”) odporne na polskie znaki.
              const roadCandidate = stripRoadPrefix(roadRaw) || extractRoadFromDisplayName(displayRaw);
              if (roadCandidate) return includesNormalized(roadCandidate, queryTrimmed);
              return includesNormalized(displayRaw, queryTrimmed);
            }
            
            return true;
            })
            .map((item) => {
            const addr = item.address || {};
            const cityRaw = addr.city || addr.town || addr.village || addr.municipality || "";
            const cityResolvedRaw = cityRaw || city || "";
            const cityResolved = capitalizeWords(cityResolvedRaw);
            const region = normalizeRegion(addr.state);
            
            let addressStr = "";
            // Ulica: preferuj addr.road, ale gdy jest zbyt uboga, spróbuj wyciągnąć z display_name
            const roadRaw = addr.road || "";
            const roadCandidate = stripRoadPrefix(roadRaw) || extractRoadFromDisplayName(item.display_name);

            // Zapamiętaj numer domu (od użytkownika lub z Nominatim)
            let finalHouseNumber: string | null = null;
            if (roadCandidate) {
              addressStr = capitalizeWords(roadCandidate);
              // Jeśli użytkownik podał numer domu w zapytaniu, użyj go; w przeciwnym razie użyj numeru z Nominatim
              if (userHouseNumber) {
                addressStr += ` ${userHouseNumber}`;
                finalHouseNumber = userHouseNumber;
              } else if (addr.house_number) {
                addressStr += ` ${addr.house_number}`;
                finalHouseNumber = addr.house_number;
              }
            }

            // Anty-śmieci: odrzuć "Polska" jako "ulicę"
            if (type === "address" && addressStr.trim().toLowerCase() === "polska") {
              addressStr = "";
            }

            // Wyświetlana nazwa
            const parts: string[] = [];
            if (type === "address") {
              if (addressStr) parts.push(addressStr);
              if (cityResolved) parts.push(cityResolved);
            } else {
              if (cityResolved) parts.push(cityResolved);
            }
            if (region) parts.push(region);
            const display = parts.join(", ");

            return {
              display,
              city: cityResolved,
              region,
              address: addressStr || undefined,
              postalCode: addr.postcode || undefined,
              lat: parseFloat(item.lat),
              lng: parseFloat(item.lon),
              // Przekaż informację o numerze domu i oryginalnej nazwie ulicy do późniejszego geokodowania
              _needsPreciseGeocode: !!finalHouseNumber,
              _streetName: roadCandidate || undefined,
              _houseNumber: finalHouseNumber || undefined,
            };
            });

        let mapped: AddressSuggestion[] = mapAddressResults(data);

        // Fallback: czasem zapytanie z dopisanym miastem zwraca 0 wyników (np. "urszuli, Warszawa, Polska"),
        // ale bez miasta już zwraca trafienia, które możemy potem przefiltrować po Warszawie.
        if (type === "address" && city?.trim() && mapped.length === 0) {
          const fallbackQuery = `${queryTrimmed}, Polska`;
          const fallbackData = await fetchNominatim(fallbackQuery);
          mapped = mapAddressResults(fallbackData);
        }

        // Usuń duplikaty po display i ogranicz do 5 wyników
        const unique = mapped
          .filter((s) => Boolean(s.display?.trim()))
          .filter(
            (item, index, self) =>
              index === self.findIndex((t) => t.display === item.display)
          )
          .sort((a, b) => {
            const aAddr = a.address || "";
            const bAddr = b.address || "";

            const aStarts = startsWithNormalized(aAddr, queryTrimmed);
            const bStarts = startsWithNormalized(bAddr, queryTrimmed);
            if (aStarts !== bStarts) return aStarts ? -1 : 1;

            const aIncludes = includesNormalized(aAddr, queryTrimmed);
            const bIncludes = includesNormalized(bAddr, queryTrimmed);
            if (aIncludes !== bIncludes) return aIncludes ? -1 : 1;

            return a.display.localeCompare(b.display, "pl");
          })
          .slice(0, 5);

        setSuggestions(unique);
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          console.error("Błąd pobierania sugestii:", err);
        }
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  }, []);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return {
    suggestions,
    isLoading,
    fetchSuggestions,
    clearSuggestions,
  };
};
